import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import {
  ServerIcon,
  UserGroupIcon,
  QueueListIcon,
  ExclamationTriangleIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import AnimatedNumber from './AnimatedNumber';

/**
 * Cluster overview summary cards for the dashboard header.
 * Shows high-level metrics: machines, users, queues, errors, alerts.
 */
export default function ClusterOverview({ data, onRefresh }) {
  const cardsRef = useRef([]);

  // Animate cards on mount
  useEffect(() => {
    if (cardsRef.current.length > 0) {
      gsap.fromTo(
        cardsRef.current.filter(Boolean),
        { opacity: 0, y: 10, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.4, stagger: 0.05, ease: 'power2.out' }
      );
    }
  }, []);

  const cards = [
    {
      id: 'machines',
      icon: ServerIcon,
      label: 'Machines',
      value: data?.machines?.online ?? '-',
      subValue: data?.machines?.total ? `/ ${data.machines.total}` : null,
      color: data?.machines?.online === data?.machines?.total ? '#66ffb7' : '#ffd666',
    },
    {
      id: 'users',
      icon: UserGroupIcon,
      label: 'Online Users',
      value: data?.users?.active ?? '-',
      color: '#8ce5ff',
    },
    {
      id: 'queued',
      icon: QueueListIcon,
      label: 'Queued Jobs',
      value: data?.queues?.total_depth ?? '-',
      color: (data?.queues?.total_depth ?? 0) > 100 ? '#ffd666' : '#66ffb7',
    },
    {
      id: 'dlq',
      icon: ExclamationTriangleIcon,
      label: 'DLQ Jobs',
      value: data?.queues?.dlq_count ?? '-',
      color: (data?.queues?.dlq_count ?? 0) > 0 ? '#ff6b6b' : '#66ffb7',
    },
    {
      id: 'alerts',
      icon: BellAlertIcon,
      label: 'Active Alerts',
      value: data?.alerts?.active ?? '-',
      color: (data?.alerts?.critical ?? 0) > 0 ? '#ff6b6b' : (data?.alerts?.active ?? 0) > 0 ? '#ffd666' : '#66ffb7',
    },
  ];

  return (
    <div className="cluster-overview">
      <div className="cluster-overview-header">
        <h3>Cluster Overview</h3>
        {onRefresh && (
          <button className="btn-refresh" onClick={onRefresh} title="Refresh">
            Refresh
          </button>
        )}
      </div>
      <div className="cluster-overview-cards">
        {cards.map((card, idx) => (
          <div
            key={card.id}
            className="cluster-card"
            ref={(el) => (cardsRef.current[idx] = el)}
            style={{ '--card-color': card.color }}
          >
            <div className="cluster-card-icon">
              <card.icon className="icon" />
            </div>
            <div className="cluster-card-value">
              <AnimatedNumber value={typeof card.value === 'number' ? card.value : 0} />
              {card.subValue && <span className="sub-value">{card.subValue}</span>}
            </div>
            <div className="cluster-card-label">{card.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
